import NiceModal from "@ebay/nice-modal-react";
import { translate } from "hooks";
import { getTopology } from "utils/wizardHelpers/wizardHelpers";

import { ConfirmationModal } from "components/_Modals/ConfirmationModal";
import type { IconName } from "components/Icon/iconNames";
import { getComputedLayout } from "components/Topology/computedLayout/getComputedLayout";
import type {
  StructuredTopology,
  Topology,
  TopologyAction,
  TopologyNode,
} from "interfaces/product/topology/topology.types";
import type {
  AddedZone,
  Questionnaire,
  Zone,
} from "interfaces/wizard/wizard.types";
import type {
  RemoveItemAndUpdatePositionsProps,
  WizardResponse,
} from "interfaces/wizard/wizardStore.types";
import type { JSX } from "react";
import type { WizardState } from "stores/wizardStore";

type ShowConfirmModalProps = {
  action: () => Promise<void> | void;
  onCancelAction?: () => void;
  iconName?: IconName;
  title?: string | JSX.Element;
  specificAlertText?: string | JSX.Element;
  specificSubmitText?: string | JSX.Element;
  specificCancelText?: string | JSX.Element;
  isWithoutCancelBtn?: boolean;
};

export const mapWizardResponseToState = (
  wizard: WizardResponse,
  subType: string | null,
): Partial<WizardState> => {
  const topology = wizard.topology as Topology[] | null | undefined;
  const structuredTopology = topology
    ? (getTopology(topology[0]?.configurations[0]) as StructuredTopology | null)
    : null;

  return {
    steps: wizard.totalSteps,
    step: wizard.currentStep,
    language: wizard.languageCode,
    nodeId: wizard.projectNodeId,
    availableSteps: wizard.availableSteps ?? undefined,
    constraints: wizard.constraints ?? undefined,
    currentQuestionnaire: wizard.currentQuestionnaire ?? undefined,
    questionnaireLists: wizard.questionnaires?.filter(
      (q): q is Questionnaire => q !== null,
    ),
    projectId: wizard.projectId,
    subType: wizard.subType,
    topology: topology ?? null,
    structuredTopology,
    computedLayoutTopology: getComputedLayout({ structuredTopology, subType }),
    possibleTopologyActions: topology?.[0]?.actions
      ? topology[0].actions.reduce<Record<number, TopologyAction>>(
          (acc, item) => {
            acc[item.id] = item;
            return acc;
          },
          {},
        )
      : null,
    nodes: wizard.nodes ?? [],
  };
};

export const showConfirmModal = ({
  action,
  onCancelAction,
  iconName = "save-regular",
  title,
  specificAlertText,
  specificSubmitText,
  specificCancelText,
  isWithoutCancelBtn,
}: ShowConfirmModalProps) => {
  NiceModal.show(ConfirmationModal, {
    iconName,
    title: title || translate("WIZARD_RESET_DATA_CONFIRMATION"),
    alertText:
      specificAlertText || translate("WIZARD_RESET_DATA_CONFIRMATION_ALERT"),
    submitBtnText:
      specificSubmitText || translate("WIZARD_RESET_DATA_CONFIRMATION_ACCEPT"),
    cancelBtnText:
      specificCancelText || translate("WIZARD_RESET_DATA_CONFIRMATION_CANCEL"),
    isWithoutCancelBtn,
    onConfirm: async () => action(),
    onCancel: () => onCancelAction && onCancelAction(),
  });
};

export function insertItemAndUpdatePositions(
  arr: Zone[],
  newItems: AddedZone[],
) {
  const clonedZonesIds = new Set(newItems.map((item) => item.id));

  const minPositionOfClonedZone = Math.min(
    ...newItems.map((item) => item.position),
  );

  const existing = arr
    .filter((item) => !clonedZonesIds.has(item.id))
    .map((item) => ({
      ...item,
      position:
        item.position >= minPositionOfClonedZone
          ? item.position + newItems.length
          : item.position,
    }));

  const merged = [...existing, ...newItems].sort(
    (a, b) => a.position - b.position,
  );

  return merged;
}

export function removeItemAndUpdatePositions({
  arr,
  itemId,
}: RemoveItemAndUpdatePositionsProps) {
  const itemIndex = arr.findIndex((item) => item.id === itemId);

  if (itemIndex === -1) {
    return arr;
  }

  const itemPosition = arr[itemIndex].position;

  return arr
    .filter((item) => item.id !== itemId)
    .map((item) => {
      if (item.position > itemPosition) {
        return { ...item, position: item.position - 1 };
      }
      return item;
    });
}

export function updateTopologyZoneTitle(
  topology: Topology[],
  zoneId: string,
  newTitle: string,
): Topology[] {
  const deepCopyAndUpdate = (items: TopologyNode[]): TopologyNode[] =>
    items.map((item) => {
      const newItem = { ...item };

      if (newItem.kind === "fam" && newItem.id === zoneId) {
        newItem.info = newTitle;
      }

      if (Array.isArray(newItem.configurations)) {
        newItem.configurations = deepCopyAndUpdate(newItem.configurations);
      }

      return newItem;
    });

  return topology.map((entry) => ({
    ...entry,
    configurations: deepCopyAndUpdate(entry.configurations),
  }));
}
